import { useVirtualizer } from '@tanstack/react-virtual';
import { ChevronDown, Search, X } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { BID_CATEGORY_ORDER } from '../data/bidCategories';
import { sortChannelsByName } from '../lib/channels';
import type { ChannelCategory, ChannelDefinition, GraphPanel } from '../types';
import { GraphIdentityBadge } from './GraphIdentityBadge';

const CATEGORY_ORDER: ChannelCategory[] = [...BID_CATEGORY_ORDER, 'Other'];

type FlatItem =
  | { type: 'heading'; category: ChannelCategory; count: number }
  | { type: 'channel'; channel: ChannelDefinition };

type ChannelBrowserProps = {
  channels: ChannelDefinition[];
  graphs: GraphPanel[];
  focusedGraphId: string | null;
  onAssign: (graphId: string, channelId: string | null, slotIndex?: number) => void;
  onClose?: () => void;
  onDragActiveChange?: (active: boolean) => void;
  onNotice: (message: string) => void;
};

export function ChannelBrowser({
  channels,
  graphs,
  focusedGraphId,
  onAssign,
  onClose,
  onDragActiveChange,
  onNotice,
}: ChannelBrowserProps) {
  const [query, setQuery] = useState('');
  const [collapsedCategories, setCollapsedCategories] = useState<Set<ChannelCategory>>(() => new Set());
  const scrollRef = useRef<HTMLDivElement>(null);
  const usage = useMemo(() => {
    const memberships = new Map<string, Array<{ graphId: string; graphOrder: number; graphNumber: number; slotIndex: number }>>();
    for (const graph of graphs) {
      for (const [slotIndex, slot] of graph.slots.entries()) {
        if (slot.channelId) {
          const channelMemberships = memberships.get(slot.channelId) ?? [];
          channelMemberships.push({
            graphId: graph.id,
            graphOrder: graph.order,
            graphNumber: graph.order + 1,
            slotIndex,
          });
          memberships.set(slot.channelId, channelMemberships);
        }
      }
    }
    for (const channelMemberships of memberships.values()) {
      channelMemberships.sort((a, b) => a.graphOrder - b.graphOrder);
    }
    return memberships;
  }, [graphs]);

  const items = useMemo<FlatItem[]>(() => {
    const normalized = query.trim().toLowerCase();
    const filtered = channels.filter((channel) =>
      !normalized || `${channel.displayName} ${channel.shortName} ${channel.unit ?? ''}`.toLowerCase().includes(normalized),
    );
    return CATEGORY_ORDER.flatMap((category) => {
      const group = sortChannelsByName(filtered.filter((channel) => channel.category === category));
      if (!group.length) return [];
      const isCollapsed = !normalized && collapsedCategories.has(category);
      return [
        { type: 'heading' as const, category, count: group.length },
        ...(isCollapsed ? [] : group.map((channel) => ({ type: 'channel' as const, channel }))),
      ];
    });
  }, [channels, collapsedCategories, query]);

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) => (items[index]?.type === 'heading' ? 32 : 30),
    overscan: 10,
  });

  const toggleChannel = (channelId: string) => {
    const focusedGraph = graphs.find((candidate) => candidate.id === focusedGraphId) ?? graphs[0];
    if (!focusedGraph) return;

    const memberships = usage.get(channelId) ?? [];
    const focusedMembership = memberships.find((membership) => membership.graphId === focusedGraph.id);
    if (focusedMembership) {
      onAssign(focusedMembership.graphId, null, focusedMembership.slotIndex);
      return;
    }

    const nextMembership = memberships[0];
    if (nextMembership) {
      onAssign(nextMembership.graphId, null, nextMembership.slotIndex);
      return;
    }

    if (focusedGraph.slots.every((slot) => slot.channelId)) {
      onNotice("The focused graph already has four channels. Press the 'Add Graph' Button to add a new graph.");
      return;
    }
    onAssign(focusedGraph.id, channelId);
  };

  return (
    <section className="channel-browser" aria-label="Datalog channels">
      <div className="panel-heading">
        <div>
          <p className="eyebrow">CHANNELS</p>
          <span
            className="channel-group-count channel-total-count"
            aria-label={`${channels.length} channels available`}
          >
            {channels.length}
          </span>
        </div>
        {onClose && (
          <button className="icon-button drawer-close" onClick={onClose} aria-label="Close channels">
            <X size={19} />
          </button>
        )}
      </div>
      <label className="search-box">
        <Search size={17} aria-hidden="true" />
        <input
          type="search"
          value={query}
          placeholder="Search name, acronym, or unit"
          onChange={(event) => setQuery(event.currentTarget.value)}
          aria-label="Search channels"
        />
        {query && (
          <button onClick={() => setQuery('')} aria-label="Clear channel search">
            <X size={15} />
          </button>
        )}
      </label>
      <div className="channel-table-header" aria-hidden="true">
        <span>Channel</span>
        <span>Unit</span>
        <span>Used in</span>
      </div>
      <div ref={scrollRef} className="channel-scroll">
        <div className="virtual-list" style={{ height: virtualizer.getTotalSize() }}>
          {virtualizer.getVirtualItems().map((row) => {
            const item = items[row.index];
            if (!item) return null;
            const memberships = item.type === 'channel' ? (usage.get(item.channel.id) ?? []) : [];
            const firstMembership = memberships[0];
            const visibleMemberships = memberships.slice(0, 2);
            const isActive = Boolean(firstMembership);
            const focusedGraph = graphs.find((candidate) => candidate.id === focusedGraphId) ?? graphs[0];
            const isInFocusedGraph = Boolean(
              focusedGraph && memberships.some((membership) => membership.graphId === focusedGraph.id),
            );
            const channelActionLabel = item.type === 'channel'
              ? isInFocusedGraph
                ? `Remove ${item.channel.displayName} from focused graph`
                : firstMembership
                  ? `Remove ${item.channel.displayName} from graph ${firstMembership.graphNumber}`
                  : `Add ${item.channel.displayName} to focused graph`
              : undefined;
            return (
              <div
                key={row.key}
                className={
                  item.type === 'heading' ? 'channel-group-heading' : `channel-row${isActive ? ' is-active' : ''}`
                }
                style={{ transform: `translateY(${row.start}px)`, height: row.size }}
                draggable={item.type === 'channel'}
                role={item.type === 'channel' ? 'button' : undefined}
                tabIndex={item.type === 'channel' ? 0 : undefined}
                aria-label={channelActionLabel}
                onClick={item.type === 'channel' ? () => toggleChannel(item.channel.id) : undefined}
                onKeyDown={
                  item.type === 'channel'
                    ? (event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          toggleChannel(item.channel.id);
                        }
                      }
                    : undefined
                }
                onDragStart={
                  item.type === 'channel'
                    ? (event) => {
                        event.dataTransfer.effectAllowed = 'copy';
                        event.dataTransfer.setData('application/x-idash-channel', item.channel.id);
                        event.dataTransfer.setData('text/plain', item.channel.displayName);
                        onDragActiveChange?.(true);
                      }
                    : undefined
                }
                onDragEnd={item.type === 'channel' ? () => onDragActiveChange?.(false) : undefined}
              >
                {item.type === 'heading' ? (
                  <button
                    type="button"
                    className="channel-group-toggle"
                    aria-expanded={!collapsedCategories.has(item.category) || Boolean(query.trim())}
                    onClick={() => {
                      setCollapsedCategories((current) => {
                        const next = new Set(current);
                        if (next.has(item.category)) next.delete(item.category);
                        else next.add(item.category);
                        return next;
                      });
                    }}
                  >
                    <span>{item.category}</span>
                    <span className="channel-group-count">{item.count}</span>
                    <ChevronDown
                      size={15}
                      className={collapsedCategories.has(item.category) && !query.trim() ? 'is-collapsed' : ''}
                      aria-hidden="true"
                    />
                  </button>
                ) : (
                  <>
                    <span
                      className="channel-name"
                      title={`${item.channel.displayName}${item.channel.shortName ? ` (${item.channel.shortName})` : ''}`}
                    >
                      {item.channel.displayName}
                      {item.channel.shortName && (
                        <span className="channel-acronym"> ({item.channel.shortName})</span>
                      )}
                    </span>
                    <span className="channel-unit">{item.channel.unit || '—'}</span>
                    <span className="channel-used-in">
                      {visibleMemberships.length ? (
                        visibleMemberships.map((membership) => (
                          <GraphIdentityBadge
                            key={membership.graphId}
                            order={membership.graphOrder}
                            ariaLabel={`Used in graph ${membership.graphNumber}`}
                          />
                        ))
                      ) : (
                        <span className="channel-unused" aria-label="Not used in a graph">—</span>
                      )}
                    </span>
                  </>
                )}
              </div>
            );
          })}
        </div>
        {!items.length && <p className="empty-list">No channels match “{query}”.</p>}
      </div>
    </section>
  );
}
